<template>
  <div class="notifi-card-container">
    <label class="email-address-label"> Email address</label>
    <InputText
      v-model="emailAddress"
      :default-value="emailAddress"
      class="email-address__input"
      @input="(event: Event) => (emailAddress = (event.target as HTMLInputElement).value)"
      @focus="emailErrorMessage = ''"
      @blur="validateInput('emailAddress')"
    />
    <label class="email-error-message"> {{ emailErrorMessage }}</label>
    <label class="phone-number-label"> Phone</label>
    <InputText
      v-model="phoneNumber"
      :default-value="phoneNumber"
      class="phone-number__input"
      placeholder="+1 xxx-xxx-xxxx"
      @input="handlePhoneChange"
      @blur="validateInput('phoneNumber')"
      @focus="phoneNumberErrorMessage = ''"
    />
    <label class="phoneNumber-error-message">
      {{ phoneNumberErrorMessage }}</label
    >
    <label class="telegram-id-label"> Telegram</label>
    <InputText
      v-model="telegramId"
      :default-value="telegramId"
      class="telegram-id__input"
      placeholder="@"
      @input="(event: Event) => (telegramId = (event.target as HTMLInputElement).value)"
      @focus="telegramErrorMessage = ''"
      @blur="validateInput('telegramId')"
    />
    <label class="telegram-error-message"> {{ telegramErrorMessage }}</label>

    <p-button
      class="notifi-next-button p-button-primary"
      :disabled="subscribeDisabled || hasErrors"
      @click="onClickNext"
    >
      <span>Next</span>
    </p-button>
  </div>
</template>

<script lang="ts" setup>
import { NotifiFrontendClient } from '@notifi-network/notifi-frontend-client';
import { computed, ref, watchEffect } from 'vue';
import { useField } from 'vee-validate';
import { useNotifiStore } from '../../stores';
import { storeToRefs } from 'pinia';
import { isValidEmail, isValidTelegram } from '../../utils/';
import { isValidPhoneNumber } from 'libphonenumber-js';

const props = defineProps<{
  signMessageParams: Parameters<NotifiFrontendClient['logIn']>[0];
}>();

const { value: phoneNumber } = useField<string>('phoneNumber');
const phoneNumberErrorMessage = ref<string>('');
const { value: emailAddress } = useField<string>('emailAddress');
const emailErrorMessage = ref<string>('');
const { value: telegramId } = useField<string>('telegramId');
const telegramErrorMessage = ref<string>('');

const hasErrors = computed(() => {
  return (
    emailErrorMessage.value !== '' ||
    phoneNumberErrorMessage.value !== '' ||
    telegramErrorMessage.value !== ''
  );
});

const subscribeDisabled = computed(() => {
  return (
    (telegramId.value === '' || !telegramId.value) &&
    (emailAddress.value === '' || !emailAddress.value) &&
    (phoneNumber.value === '' || !phoneNumber.value)
  );
});

const notifiStore = useNotifiStore();

const validateInput = (field: string) => {
  switch (field) {
    case 'phoneNumber':
      if (
        phoneNumber.value !== '' &&
        isValidPhoneNumber(phoneNumber.value) === false
      ) {
        phoneNumberErrorMessage.value =
          'Invalid phone number. Please try again';
      }
      break;
    case 'emailAddress':
      if (
        emailAddress.value !== '' &&
        isValidEmail(emailAddress.value) === false
      ) {
        emailErrorMessage.value = 'Invalid email. Please try again';
      }
      break;
    case 'telegramId':
      if (
        telegramId.value !== '' &&
        isValidTelegram(telegramId.value) === false
      ) {
        telegramErrorMessage.value = 'Invalid telegram. Please try again';
      }
      break;
  }
};

const {
  emailAddress: notifiEmailAddress,
  telegramId: notifiTelegramId,
  phoneNumber: notifiPhoneNumber,
} = storeToRefs(notifiStore);

watchEffect(() => {
  if (notifiEmailAddress.value !== '') {
    emailAddress.value = notifiEmailAddress.value;
  }

  if (notifiTelegramId.value !== '') {
    telegramId.value = notifiTelegramId.value;
  }

  if (notifiPhoneNumber.value !== '') {
    phoneNumber.value = notifiPhoneNumber.value;
  }
});

const areInputFieldsUnchanged = computed(() => {
  return (
    notifiPhoneNumber.value === phoneNumber.value &&
    notifiTelegramId.value === telegramId.value &&
    emailAddress.value === notifiEmailAddress.value
  );
});

async function onClickNext() {
  const client = notifiStore.notifiClient;
  const userState = notifiStore.userState;

  if (client === null || userState === undefined) {
    return;
  }
  if (areInputFieldsUnchanged.value) {
    notifiStore.setCardState('preview');
    return;
  }

  const emailAddressInput =
    emailAddress.value === '' ? undefined : emailAddress.value;
  const telegramIdInput =
    telegramId.value === '' ? undefined : telegramId.value;
  const phoneNumberInput =
    phoneNumber.value === '' ? undefined : phoneNumber.value;

  if (userState.status !== 'authenticated') {
    await client.logIn(props.signMessageParams);
  }

  const ensureTGResult = await client.ensureTargetGroup({
    emailAddress: emailAddressInput,
    telegramId: telegramIdInput,
    phoneNumber: phoneNumberInput,
    name: 'Default',
  });

  if (ensureTGResult) {
    notifiStore.setIsFTU(false);
    notifiStore.handleUpdatingTargetGroupState(ensureTGResult);

    notifiStore.setCardState('preview');
  }
}

const handlePhoneChange = (event: Event) => {
  const onlyNumberInput = (event.target as HTMLTextAreaElement).value.replace(
    /[^\d+-]/g,
    '',
  );

  phoneNumber.value = onlyNumberInput;
};
</script>

<style scoped lang="scss">
.notifi-card-container {
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  padding: 0px 24px 0px 24px;
}

.phone-number-label,
.email-address-label,
.telegram-id-label {
  font-family: Roboto;
  font-size: 15px;
  font-weight: 500;
  color: #e5e4fa;
  opacity: 0.5;
  margin-bottom: 12px;
}
.phone-number__input,
.email-address__input,
.telegram-id__input {
  margin-top: 6px;
  font-family: Roboto;
  margin-bottom: 20px;
  width: 352px;
  background: none;
  border: 1px solid #36354b;
}
.phone-number__input::placeholder,
.email-address__input::placeholder,
.telegram-id__input::placeholder {
  opacity: 0.5;
  color: #e5e4fa;
}

.email-error-message,
.telegram-error-message,
.phoneNumber-error-message {
  position: relative;
  top: -15px;
  font-size: 12px;
  color: #c86060;
}
</style>
